import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import i18n, {
  LANGUAGE_STORAGE_KEY,
  LANGUAGE_OPTIONS,
  normalizeLanguage,
  type SupportedLanguage,
} from '../i18n';
import { api } from '../api';
import { isAuthenticated } from '../auth';

interface LanguageContextType {
  language: SupportedLanguage;
  options: typeof LANGUAGE_OPTIONS;
  /** Switch the UI language. Set persistToServer when the user is logged in
   *  so the choice survives across devices (writes PATCH /auth/me). */
  setLanguage: (lang: SupportedLanguage, persistToServer?: boolean) => Promise<void>;
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  options: LANGUAGE_OPTIONS,
  setLanguage: async () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<SupportedLanguage>(
    normalizeLanguage(i18n.language),
  );

  const setLanguage = useCallback(async (lang: SupportedLanguage, persistToServer = false) => {
    await i18n.changeLanguage(lang);
    try { localStorage.setItem(LANGUAGE_STORAGE_KEY, lang); } catch { /* ignore */ }
    setLanguageState(lang);
    if (persistToServer) {
      try { await api.patch('/auth/me', { preferredLanguage: lang }); } catch { /* best-effort */ }
    }
  }, []);

  // On load, if the user is already authenticated, adopt the language they
  // saved on the server so it follows them to new devices/browsers.
  useEffect(() => {
    let cancelled = false;
    isAuthenticated().then(authed => {
      if (!authed) return;
      api.get('/auth/me').then(res => {
        const serverLang = normalizeLanguage(res.data?.preferredLanguage);
        if (!cancelled && serverLang !== normalizeLanguage(i18n.language)) {
          setLanguage(serverLang);
        }
      }).catch(() => { /* ignore */ });
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [setLanguage]);

  // Keep React state in lockstep if something changes i18n directly.
  useEffect(() => {
    const handler = (lng: string) => setLanguageState(normalizeLanguage(lng));
    i18n.on('languageChanged', handler);
    return () => { i18n.off('languageChanged', handler); };
  }, []);

  return (
    <LanguageContext.Provider value={{ language, options: LANGUAGE_OPTIONS, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
